import { ChevronLeft, ChevronRight } from 'lucide-react';
import React from 'react';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    variant?: 'full' | 'simple';
    className?: string;
}

const BTN_BASE =
    'relative inline-flex items-center border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700';

const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    hasPrevious,
    hasNext,
    onPrevious,
    onNext,
    variant = 'full',
    className = '',
}) => {
    if (totalPages <= 1) {
        return null;
    }

    const pageLabel = (
        <p className="text-sm text-gray-700 dark:text-neutral-300">
            Página <span className="font-medium">{currentPage}</span> de{' '}
            <span className="font-medium">{totalPages}</span>
        </p>
    );

    if (variant === 'simple') {
        return (
            <div
                className={`flex items-center justify-between border-t border-gray-200 pt-4 dark:border-neutral-700 ${className}`}
            >
                {pageLabel}
                <nav
                    className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                    aria-label="Pagination"
                >
                    <button
                        onClick={onPrevious}
                        disabled={!hasPrevious}
                        className={`${BTN_BASE} rounded-l-md px-3`}
                    >
                        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                        <span className="ml-1 hidden sm:inline">Anterior</span>
                    </button>
                    <button
                        onClick={onNext}
                        disabled={!hasNext}
                        className={`${BTN_BASE} rounded-r-md px-3`}
                    >
                        <span className="mr-1 hidden sm:inline">Siguiente</span>
                        <ChevronRight className="h-5 w-5" aria-hidden="true" />
                    </button>
                </nav>
            </div>
        );
    }

    return (
        <div
            className={`flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4 dark:border-neutral-700 dark:bg-neutral-800 ${className}`}
        >
            {/* Mobile */}
            <div className="flex flex-1 justify-between sm:hidden">
                <button
                    onClick={onPrevious}
                    disabled={!hasPrevious}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                >
                    Anterior
                </button>
                <button
                    onClick={onNext}
                    disabled={!hasNext}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                >
                    Siguiente
                </button>
            </div>

            {/* Desktop */}
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>{pageLabel}</div>
                <div>
                    <nav
                        className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                        aria-label="Pagination"
                    >
                        <button
                            onClick={onPrevious}
                            disabled={!hasPrevious}
                            className={`${BTN_BASE} rounded-l-md`}
                        >
                            <span className="sr-only">Anterior</span>
                            <ChevronLeft
                                className="h-5 w-5"
                                aria-hidden="true"
                            />
                        </button>
                        <button
                            onClick={onNext}
                            disabled={!hasNext}
                            className={`${BTN_BASE} rounded-r-md`}
                        >
                            <span className="sr-only">Siguiente</span>
                            <ChevronRight
                                className="h-5 w-5"
                                aria-hidden="true"
                            />
                        </button>
                    </nav>
                </div>
            </div>
        </div>
    );
};

export default Pagination;
